import { Component, Input, forwardRef, OnInit } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';

@Component({
  selector: 'app-datepicker',
  templateUrl: './datepicker.component.html',
  styleUrls: ['./datepicker.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DatepickerComponent),
      multi: true,
    },
  ],
})
export class DatepickerComponent implements ControlValueAccessor, OnInit {
  @Input() label: string = '';
  @Input() placeholder: string = 'Select date';
  @Input() name: string = '';
  @Input() required: boolean = false;
  @Input() disabled: boolean = false;
  @Input() readonly: boolean = false;
  @Input() minDate: Date | null = null;
  @Input() maxDate: Date | null = null;
  @Input() format: string = 'yyyy-MM-dd';
  @Input() class: string = '';
  @Input() id: string = '';

  value: Date | null = null;
  touched: boolean = false;
  isOpen: boolean = false;

  // ControlValueAccessor implementation
  onChange = (value: Date | null) => {};
  onTouched = () => {};

  ngOnInit(): void {
    // Material datepicker handles display automatically
  }

  writeValue(value: any): void {
    // Convert string date to Date object for display
    if (value && typeof value === 'string') {
      // Parse date string (YYYY-MM-DD) to Date object in local timezone
      const dateParts = value.split('T')[0].split('-');
      if (dateParts.length === 3) {
        this.value = new Date(parseInt(dateParts[0]), parseInt(dateParts[1]) - 1, parseInt(dateParts[2]));
      } else {
        this.value = null;
      }
    } else if (value instanceof Date) {
      this.value = value;
    } else {
      this.value = null;
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onDateChange(event: MatDatepickerInputEvent<Date>): void {
    const date = event.value;
    this.value = date;

    // Convert Date to date-only string (YYYY-MM-DD) to avoid timezone conversion
    if (date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const dateString = `${year}-${month}-${day}`;
      this.onChange(dateString as any);
    } else {
      this.onChange(null);
    }
  }

  onPickerOpenedChange(opened: boolean): void {
    this.isOpen = opened;
    // If the picker is closed and we're not disabled/readonly, we can reopen it on next focus
  }

  onBlur(): void {
    this.touched = true;
    this.onTouched();
    this.isOpen = false;
  }

  onFocus(): void {
    if (!this.touched) {
      this.touched = true;
    }
    // Open the datepicker popup when input is focused
    if (!this.disabled && !this.readonly) {
      this.isOpen = true;
    }
  }

  openDatepicker(): void {
    if (!this.disabled && !this.readonly) {
      this.isOpen = true;
    }
  }

  clearDate(): void {
    this.value = null;
    this.onChange(null);
  }
}
